class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.dialog.addEventListener('click', this.onClick.bind(this));
    this.dialog.addEventListener('close', () => {
      this.showView('cart');
      document.body.classList.remove('overflow-hidden');
    });
    this.setHeaderCartIconAccessibility();
    this.refresh();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (!this.dialog.open) {
      window.openSheet(this.dialog, this.activeElement || document.activeElement);
      document.body.classList.add('overflow-hidden');
      // cart-drawer-items skips the automatic view event, the drawer sends it on open.
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    }
    this.querySelector('#CartDrawer-Heading')?.focus();
  }

  close() {
    this.dialog.close();
  }

  onClick(event) {
    if (event.target.closest('[data-cart-back]')) {
      this.showView('cart');
      this.alternativesOpener?.focus();
      return;
    }
    const add = event.target.closest('[data-upsell-add]');
    if (add) this.addUpsell(add);
  }

  showView(name) {
    this.querySelectorAll('[data-cart-view]').forEach((view) => {
      view.hidden = view.dataset.cartView !== name;
    });
  }

  showAlternatives(key, opener) {
    const panel = this.querySelector(`[data-alternatives-for="${CSS.escape(key)}"]`);
    if (!panel) return;
    this.alternativesOpener = opener;
    this.querySelectorAll('[data-alternatives-for]').forEach((item) => {
      item.hidden = item !== panel;
    });
    this.showView('alternatives');
    panel.closest('.sheet__main').scrollTop = 0;
    panel.querySelector('h2')?.focus();
    window.loadSimilarProducts(panel.querySelector('[data-similar]'));
  }

  refresh() {
    this.observer?.disconnect();
    const scroll = this.querySelector('[data-cart-scroll]');
    const footer = this.querySelector('[data-cart-footer]');
    const sticky = this.querySelector('[data-cart-sticky]');
    if (scroll && footer && sticky) {
      sticky.querySelector('[data-cart-sticky-value]').textContent =
        footer.querySelector('[data-cart-total]')?.dataset.cartTotal || '';
      this.observer = new IntersectionObserver(
        ([entry]) => {
          sticky.hidden = entry.isIntersecting;
        },
        { root: scroll }
      );
      this.observer.observe(footer);
    }
    this.loadUpsell();
  }

  async loadUpsell() {
    const box = this.querySelector('[data-upsell]');
    if (!box || box.dataset.loaded) return;
    box.dataset.loaded = 'true';

    try {
      const exclude = box.dataset.exclude.split(',').map(Number);
      const pick = (products) => {
        for (const product of products || []) {
          if (exclude.includes(product.id)) continue;
          const variant = product.variants.find((item) => item.available);
          if (variant) return { product, variant };
        }
        return null;
      };
      let match = pick((await (await fetch(box.dataset.url)).json()).products);
      // Collection products.json: prices in units as strings, images instead of featured_image.
      if (!match && box.dataset.fallbackUrl) {
        match = pick((await (await fetch(box.dataset.fallbackUrl)).json()).products);
        if (match) {
          match.product.url = `${window.Shopify?.routes?.root || '/'}products/${match.product.handle}`;
          match.product.featured_image = match.product.images?.[0]?.src;
          match.variant.price = Math.round(parseFloat(match.variant.price) * 100);
        }
      }
      if (!match) return;
      const { product, variant } = match;

      const format = new Intl.NumberFormat(document.documentElement.lang || 'de', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
        minimumFractionDigits: 0,
      });
      box.querySelectorAll('[data-upsell-link]').forEach((link) => {
        link.href = product.url;
      });
      box.querySelector('[data-upsell-name]').textContent = product.title;
      box.querySelector('[data-upsell-price]').textContent = format.format(variant.price / 100);
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=200`;
        image.alt = '';
        image.loading = 'lazy';
        box.querySelector('.cart-upsell__media').replaceChildren(image);
      }
      const add = box.querySelector('[data-upsell-add]');
      add.dataset.variant = variant.id;
      add.dataset.title = product.title;
      add.setAttribute('aria-label', add.dataset.label.replace('[title]', product.title));
      box.hidden = false;
    } catch (error) {
      console.error(error);
    }
  }

  async addUpsell(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');

    try {
      const response = await fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variant), quantity: 1 }],
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message);

      const title = button.dataset.title;
      this.renderContents(data);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: data });
      this.querySelector('[data-upsell-add]')?.focus();
      const status = this.querySelector('#CartDrawer-LiveRegionText');
      if (status) status.textContent = window.cartStrings.liveAdded.replace('[title]', title);
    } catch (error) {
      console.error(error);
      button.removeAttribute('aria-busy');
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.refresh();
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    this.closest('cart-drawer')?.refresh?.();
  }

  // Add to cart from product forms and the drawer itself already rendered the drawer.
  onCartUpdate(event) {
    if (['product__form', 'product-form', 'cart-drawer'].includes(event?.source)) return;
    const dialog = this.closest('dialog');
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const source = new DOMParser().parseFromString(responseText, 'text/html').querySelector('#CartDrawer');
        if (!source || !dialog) return;
        const hadFocus = dialog.contains(document.activeElement);
        dialog.innerHTML = source.innerHTML;
        if (hadFocus) dialog.querySelector('#CartDrawer-Heading')?.focus();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
